import { createContext, useContext, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { mergeWidgetHeaderConfig, widgetHeaderConfigsEqual } from './widgetHeaderConfig.js';

export interface WidgetHeaderConfig {
  title?: ReactNode;
  menuItems?: ReactNode;
  toolbarEnd?: ReactNode;
}

interface WidgetHeaderContextValue {
  setHeaderConfig: (config: WidgetHeaderConfig) => void;
}

const WidgetHeaderContext = createContext<WidgetHeaderContextValue | null>(null);

export function WidgetHeaderProvider({ children, setHeaderConfig }: { children: ReactNode; setHeaderConfig: (config: WidgetHeaderConfig) => void }) {
  const value = useMemo(() => ({ setHeaderConfig }), [setHeaderConfig]);
  return <WidgetHeaderContext.Provider value={value}>{children}</WidgetHeaderContext.Provider>;
}

/** Lets a widget customize the shared header title, menu, and toolbar actions (must run under WidgetHeaderProvider). */
export function useWidgetHeader(config: WidgetHeaderConfig, deps: React.DependencyList) {
  const context = useContext(WidgetHeaderContext);
  const setHeaderConfigRef = useRef(context?.setHeaderConfig);
  setHeaderConfigRef.current = context?.setHeaderConfig;

  const configRef = useRef(config);
  configRef.current = config;

  const lastPublishedRef = useRef<WidgetHeaderConfig>({});

  // useLayoutEffect so the shared header title updates before paint (useEffect caused blank titles).
  useLayoutEffect(() => {
    const set = setHeaderConfigRef.current;
    if (!set) return;

    const merged = mergeWidgetHeaderConfig(lastPublishedRef.current, configRef.current);
    if (widgetHeaderConfigsEqual(lastPublishedRef.current, merged)) {
      return;
    }

    lastPublishedRef.current = merged;
    set(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useLayoutEffect(() => {
    return () => {
      lastPublishedRef.current = {};
      setHeaderConfigRef.current?.({ menuItems: undefined, toolbarEnd: undefined });
    };
  }, []);
}
